import React from "react";
import { Card, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStore, faBuilding, faCheck } from "@fortawesome/free-solid-svg-icons";
import { useEmpresas } from "providers/EmpresaProvider";
import AccessDeniedOverlay from "components/modules/common/AccessDeniedOverlay";

interface LojasAssociadasPromocaoProps {
  // Aceita id numérico (Promoção/legado) ou Guid string (Tabela de Preços)
  selectedLojas: Array<string | number>;
  onToggleLoja: (lojaId: string | number) => void;
  titulo?: string;
  legenda?: React.ReactNode;
}

const formatCnpj = (cnpj: string) => {
  if (!cnpj) return "";
  return cnpj.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5"
  );
};

const LojasAssociadasPromocao: React.FC<LojasAssociadasPromocaoProps> = ({
  selectedLojas = [], // Valor padrão para garantir que nunca seja undefined
  onToggleLoja,
  titulo = "Lojas Associadas",
  legenda,
}) => {
  const { empresas, isLoading } = useEmpresas();

  return (
    <Card className="mb-4">
      <Card.Body>
        <h5 className="mb-3">
          <FontAwesomeIcon icon={faStore} className="me-2" />
          {titulo}
        </h5>
        {legenda && <p className="text-body-secondary small mb-3">{legenda}</p>}

        {isLoading ? (
          <div className="text-center py-4">
            <Spinner
              animation="border"
              role="status"
              size="sm"
              className="me-2"
            />
            <span>Carregando lojas...</span>
          </div>
        ) : !empresas || empresas.length === 0 ? (
            <AccessDeniedOverlay 
              message="Você não tem permissão para consultar ou visualizar lojas."
              compact
            >
              <div className="alert alert-info">
                Nenhuma loja disponível no momento.
              </div>
            </AccessDeniedOverlay>
        ) : (
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
            {empresas.map((empresa) => {
              const isSelected = selectedLojas.includes(empresa.id);

              return (
                <div className="col" key={empresa.id}>
                  <div
                    className={`card h-100 border-2 shadow-sm ${
                      isSelected
                        ? "border-primary bg-primary-subtle"
                        : "border-translucent"
                    }`}
                    style={{
                      transition: "all 0.15s ease-in-out",
                      cursor: "pointer",
                    }}
                    onClick={() => onToggleLoja(empresa.id)}
                    onMouseEnter={(e) => {
                      if (!isSelected)
                        e.currentTarget.classList.add("border-primary");
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected)
                        e.currentTarget.classList.remove("border-primary");
                    }}
                  >
                    {/* Indicador de seleção (sempre visível) — comunica que o card é clicável */}
                    <div
                      className={`position-absolute d-flex justify-content-center align-items-center ${
                        isSelected
                          ? "bg-primary text-white border-primary"
                          : "bg-body text-transparent border-2 border-translucent"
                      }`}
                      style={{
                        top: "8px",
                        right: "8px",
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        border: isSelected ? undefined : "2px solid var(--phoenix-border-color)",
                        zIndex: 1,
                      }}
                    >
                      {isSelected && <FontAwesomeIcon icon={faCheck} size="xs" />}
                    </div>

                    <div className="card-body d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <div
                          className={`d-flex justify-content-center align-items-center me-3 ${
                            isSelected ? "" : "text-muted"
                          }`}
                          style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: "#f8f9fa",
                            borderRadius: "8px",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faBuilding}
                            size="lg"
                          />
                        </div>
                        <div>
                          <h6 className="fw-bold mb-0">
                            {empresa.nomeFantasia}
                          </h6>
                          <small>{formatCnpj(empresa.cnpj)}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default LojasAssociadasPromocao;
